import { useEffect, useRef, useState } from "react";
import {
  Store, Building2, Globe, Instagram, Facebook, Image as ImageIcon, Upload,
  Trash2, Save, Loader2, Lock, Tag, X, Clock, ExternalLink, Sparkles, MapPin,
} from "lucide-react";
import { toast } from "sonner";
import CockpitLayout from "@/components/admin/CockpitLayout";
import { api, fileUrl } from "@/lib/api";

const GOLD = "#D89A00", INK = "#2C2E33", SLATE = "#777F88", MUTED = "#5B6472", LINE = "#E7E1D6";
const inputCls = "fundio-input mt-1.5 disabled:bg-fundio-subtle disabled:text-fundio-slate";
const labelCls = "block text-xs font-semibold uppercase tracking-wide text-fundio-slate";

const ROLE_RANK = { owner: 4, admin: 3, editor: 2, employee: 1 };
const PLAN_LABEL = { FREE: "FREE", LIGHT: "LIGHT", WEBPRO: "WEBPRO", MULTI: "MULTI" };
const PUB_LABEL = { unpublished: "Nicht veröffentlicht", published: "Veröffentlicht" };

const PUBLISHING = {
  FREE: { title: "Verzeichniseintrag", desc: "Dein Brocki erscheint im FUNDIO-Verzeichnis." },
  LIGHT: { title: "Verzeichniseintrag + Mini-Website", desc: "Zusätzlich eine eigene Mini-Website unter deiner Subdomain." },
  WEBPRO: { title: "Verzeichniseintrag + Mini-Website + eigene Domain", desc: "Wie LIGHT, plus deine eigene Wunsch-Domain." },
  MULTI: { title: "Verzeichnis + Mini-Website + eigene Domain · Mehrere Standorte", desc: "Alle WEBPRO-Funktionen plus zentrale Verwaltung mehrerer Standorte." },
};

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div className="fundio-panel" data-testid={testid}>
      <h2 className="mb-4 flex items-center gap-2 text-[16px] font-bold" style={{ color: INK }}>
        <Icon size={17} style={{ color: GOLD }} /> {title}
      </h2>
      {children}
    </div>
  );
}

function ReadRow({ label, children, testid }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-fundio-border/60 py-2.5 last:border-0 sm:flex-row sm:items-center sm:justify-between">
      <span className="text-[13px] font-medium" style={{ color: SLATE }}>{label}</span>
      <span className="text-[13.5px] font-semibold" style={{ color: INK }} data-testid={testid}>{children}</span>
    </div>
  );
}

export default function MeinBrocki() {
  const [t, setT] = useState(null);          // tenant/org data
  const [role, setRole] = useState(null);
  const [form, setForm] = useState(null);    // editable fields
  const [catInput, setCatInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [noAccess, setNoAccess] = useState(false);
  const [contact, setContact] = useState(null);
  const [locCtx, setLocCtx] = useState(null); // MULTI location context
  const fileRef = useRef(null);

  const rank = ROLE_RANK[role] || 0;
  const canContent = rank >= ROLE_RANK.editor;   // owner/admin/editor
  const canIdentity = rank >= ROLE_RANK.admin;   // owner/admin

  const load = () => {
    api.get("/org/current").then(({ data }) => {
      if (data.status !== "ok") { setNoAccess(true); return; }
      const tn = data.tenant;
      setT(tn);
      setRole(tn.role);
      setLogoUrl(tn.logo_url ? fileUrl(tn.logo_url) : null);
      setForm({
        company_name: tn.company_name || "",
        short_description: tn.short_description || "",
        description: tn.description || "",
        address: tn.address || "",
        external_website: tn.external_website || "",
        categories: tn.categories || [],
        social_instagram: tn.social_instagram || "",
        social_facebook: tn.social_facebook || "",
      });
      if (tn.account_mode === "multi") {
        api.get("/me/locations").then(({ data: lc }) => setLocCtx(lc)).catch(() => {});
      }
    }).catch(() => setNoAccess(true));
    api.get("/auth/contact").then(({ data }) => setContact(data)).catch(() => setContact(null));
  };

  useEffect(load, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const addCat = () => {
    const v = catInput.trim();
    if (!v) return;
    if (!form.categories.some((c) => c.toLowerCase() === v.toLowerCase()))
      set("categories", [...form.categories, v]);
    setCatInput("");
  };
  const removeCat = (i) => set("categories", form.categories.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        short_description: form.short_description, description: form.description,
        categories: form.categories, social_instagram: form.social_instagram,
        social_facebook: form.social_facebook,
      };
      if (canIdentity) {
        body.company_name = form.company_name;
        body.address = form.address;
        body.external_website = form.external_website;
      }
      await api.put("/org/current/profile", body);
      toast.success("Firmenprofil gespeichert.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Speichern"); }
    finally { setSaving(false); }
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/org/current/logo", fd);
      setLogoUrl(fileUrl(data.logo_url));
      toast.success("Logo aktualisiert.");
    } catch (err) { toast.error(err.response?.data?.detail || "Logo-Upload fehlgeschlagen"); }
    finally { setLogoBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    try { await api.delete("/org/current/logo"); setLogoUrl(null); toast.success("Logo entfernt."); }
    catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setLogoBusy(false); }
  };

  const field = (label, key, ph, editable) => (
    <div>
      <label className={labelCls}>{label}</label>
      <input data-testid={`brocki-${key}`} value={form[key] || ""} onChange={(e) => set(key, e.target.value)}
        placeholder={ph} className={inputCls} disabled={!editable} />
    </div>
  );

  const plan = t?.plan || "FREE";
  const pub = PUBLISHING[plan] || PUBLISHING.FREE;
  const isFree = plan === "FREE";
  const websiteLive = !!t?.website_enabled && !isFree;
  const isMulti = t?.account_mode === "multi";
  // The "selected business/location" shown must stay consistent with the active
  // scope in the topbar: in MULTI, that is the chosen location (or "Alle Standorte").
  const selectedLocation = (() => {
    if (!isMulti || !locCtx) return null;
    const active = (locCtx.locations || []).find((l) => l.id === locCtx.active_location_id);
    if (active) return active.name;
    return locCtx.can_view_all ? "Alle Standorte" : ((locCtx.locations || [])[0]?.name || "—");
  })();

  return (
    <CockpitLayout active="mein-brocki" title="Mein Brocki">
      <div className="fundio-page-stack">
        <div className="fundio-section-head">
          <div>
            <h1 className="fundio-page-title flex items-center gap-2.5" style={{ color: INK }} data-testid="mein-brocki-title">
              <Store size={28} style={{ color: GOLD }} /> Mein Brocki
            </h1>
            <p className="mt-1 fundio-muted text-[14px]">Dein zentrales Firmenprofil für Verzeichnis und Website.</p>
          </div>
        </div>

        {noAccess ? (
          <div className="fundio-panel" style={{ color: MUTED }} data-testid="mein-brocki-no-access">
            Kein Brocki-Zugang.
          </div>
        ) : !t || !form ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin" style={{ color: GOLD }} /></div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-3">
            {/* left: editable profile */}
            <div className="space-y-6 lg:col-span-2">
              {!canContent && (
                <div className="fundio-panel flex items-center gap-2.5" style={{ color: MUTED }} data-testid="brocki-readonly-hint">
                  <Lock size={16} style={{ color: SLATE }} /> Du hast Lesezugriff. Änderungen können Inhaber, Admins und Redaktion vornehmen.
                </div>
              )}

              <Section icon={ImageIcon} title="Logo" testid="brocki-logo-section">
                <div className="flex items-center gap-5">
                  <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl border bg-fundio-subtle" style={{ borderColor: LINE }} data-testid="brocki-logo-preview">
                    {logoUrl ? <img src={logoUrl} alt="Logo" className="h-full w-full object-contain" /> : <Building2 size={26} style={{ color: SLATE }} />}
                  </div>
                  <div>
                    <p className="text-[13px]" style={{ color: MUTED }}>Wird im FUNDIO-Verzeichniseintrag angezeigt. PNG/JPG/WebP.</p>
                    {canIdentity ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onLogo} data-testid="brocki-logo-input" />
                        <button onClick={() => fileRef.current?.click()} disabled={logoBusy} data-testid="brocki-logo-upload"
                          className="fundio-action fundio-action--gold" style={{ minHeight: 38, padding: "8px 14px", fontSize: 14 }}>
                          {logoBusy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {logoUrl ? "Ersetzen" : "Hochladen"}
                        </button>
                        {logoUrl && (
                          <button onClick={removeLogo} disabled={logoBusy} data-testid="brocki-logo-remove"
                            className="fundio-action" style={{ minHeight: 38, padding: "8px 14px", fontSize: 14, color: "#903824", borderColor: "#E7B9AF" }}>
                            <Trash2 size={14} /> Entfernen
                          </button>
                        )}
                      </div>
                    ) : <p className="mt-2 text-xs" style={{ color: SLATE }}>Logo-Verwaltung: Inhaber/Admin.</p>}
                  </div>
                </div>
              </Section>

              <Section icon={Building2} title="Firmenprofil" testid="brocki-profile-section">
                <div className="grid gap-3 sm:grid-cols-2">
                  {field("Firmenname", "company_name", "Fundgrube AZ", canIdentity)}
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Kurzbeschreibung</label>
                    <input data-testid="brocki-short_description" value={form.short_description} onChange={(e) => set("short_description", e.target.value)}
                      placeholder="In einem Satz: was macht dein Brocki besonders?" className={inputCls} disabled={!canContent} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Ausführliche Beschreibung</label>
                    <textarea data-testid="brocki-description" value={form.description} onChange={(e) => set("description", e.target.value)}
                      rows={5} placeholder="Erzähle mehr über dein Brocki, Sortiment und Geschichte." className={inputCls} disabled={!canContent} />
                  </div>
                  {field("Adresse", "address", "Strasse, PLZ Ort", canIdentity)}
                  {field("Externe Website", "external_website", "https://…", canIdentity)}
                </div>
              </Section>

              <Section icon={Tag} title="Kategorien" testid="brocki-categories-section">
                <div className="flex flex-wrap gap-2" data-testid="brocki-categories-list">
                  {form.categories.length === 0 && <span className="text-sm" style={{ color: SLATE }}>Noch keine Kategorien.</span>}
                  {form.categories.map((c, i) => (
                    <span key={i} className="flex items-center gap-1.5 rounded-full bg-fundio-gold/10 px-3 py-1 text-[13px] font-medium" style={{ color: INK }} data-testid={`brocki-cat-${i}`}>
                      {c}
                      {canContent && <button onClick={() => removeCat(i)} data-testid={`brocki-cat-remove-${i}`} className="text-fundio-slate hover:text-red-600"><X size={13} /></button>}
                    </span>
                  ))}
                </div>
                {canContent && (
                  <div className="mt-3 flex gap-2">
                    <input data-testid="brocki-cat-input" value={catInput} onChange={(e) => setCatInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCat(); } }}
                      placeholder="z.B. Möbel, Vintage, Bücher" className="fundio-input" />
                    <button onClick={addCat} data-testid="brocki-cat-add" className="fundio-action shrink-0">Hinzufügen</button>
                  </div>
                )}
              </Section>

              <Section icon={Instagram} title="Social Links" testid="brocki-social-section">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className={labelCls}><span className="inline-flex items-center gap-1"><Instagram size={12} /> Instagram</span></label>
                    <input data-testid="brocki-social_instagram" value={form.social_instagram} onChange={(e) => set("social_instagram", e.target.value)}
                      placeholder="https://instagram.com/…" className={inputCls} disabled={!canContent} />
                  </div>
                  <div>
                    <label className={labelCls}><span className="inline-flex items-center gap-1"><Facebook size={12} /> Facebook</span></label>
                    <input data-testid="brocki-social_facebook" value={form.social_facebook} onChange={(e) => set("social_facebook", e.target.value)}
                      placeholder="https://facebook.com/…" className={inputCls} disabled={!canContent} />
                  </div>
                </div>
              </Section>

              {canContent && (
                <button data-testid="brocki-save-btn" onClick={save} disabled={saving} className="fundio-action fundio-action--gold w-fit">
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Profil speichern
                </button>
              )}
            </div>

            {/* right: status + publishing + contact (read-only) */}
            <div className="space-y-6">
              <Section icon={Sparkles} title="Status & Paket" testid="brocki-status-section">
                <ReadRow label="Betrieb" testid="brocki-view-company">{t.company_name}</ReadRow>
                {isMulti && selectedLocation && (
                  <ReadRow label="Ausgewählter Standort" testid="brocki-view-location">
                    <span className="inline-flex items-center gap-1.5"><MapPin size={13} style={{ color: GOLD }} /> {selectedLocation}</span>
                  </ReadRow>
                )}
                <ReadRow label={isMulti ? "Organisationspaket" : "Paket"} testid="brocki-view-plan">
                  <span className="rounded-full bg-fundio-gold/15 px-2.5 py-0.5 text-[12px] font-bold text-fundio-gold">{PLAN_LABEL[plan] || plan}</span>
                </ReadRow>
                <ReadRow label="Slug" testid="brocki-view-slug"><span className="font-mono text-[12.5px]">{t.slug}</span></ReadRow>
                <ReadRow label="Status" testid="brocki-view-status">{t.status === "active" ? "Aktiv" : t.status}</ReadRow>
                <ReadRow label="Veröffentlichung" testid="brocki-view-publishing">{PUB_LABEL[t.publishing_status] || t.publishing_status}</ReadRow>
                <ReadRow label="Website aktiv" testid="brocki-view-website">{websiteLive ? "Ja" : "Nein"}</ReadRow>
              </Section>

              <Section icon={Globe} title="Publishing-Vorschau" testid="brocki-publishing-section">
                <p className="text-[14px] font-bold" style={{ color: INK }} data-testid="brocki-pub-title">{pub.title}</p>
                <p className="mt-1 text-[13px]" style={{ color: MUTED }}>{pub.desc}</p>

                <div className="mt-4 space-y-2.5">
                  <div className="rounded-lg border p-3" style={{ borderColor: LINE }} data-testid="brocki-subdomain-box">
                    <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: SLATE }}>Subdomain</p>
                    <p className="mt-0.5 font-mono text-[13px]" style={{ color: INK }}>{t.subdomain || "—"}</p>
                    {isFree ? (
                      <p className="mt-1 flex items-center gap-1.5 text-[12px] font-medium" style={{ color: SLATE }} data-testid="brocki-subdomain-reserved">
                        <Lock size={12} /> Intern reserviert · noch nicht aktiv (FREE)
                      </p>
                    ) : websiteLive ? (
                      <span className="mt-1 inline-flex items-center gap-1 text-[12px] font-medium text-status-success">Aktiv</span>
                    ) : (
                      <span className="mt-1 inline-block text-[12px]" style={{ color: SLATE }}>Bereit · noch nicht live geschaltet</span>
                    )}
                  </div>

                  {(plan === "WEBPRO" || plan === "MULTI") && (
                    <div className="rounded-lg border p-3" style={{ borderColor: LINE }} data-testid="brocki-customdomain-box">
                      <p className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: SLATE }}>Eigene Domain</p>
                      <p className="mt-0.5 font-mono text-[13px]" style={{ color: INK }}>{t.custom_domain || "Noch keine Domain hinterlegt"}</p>
                      <span className="mt-1 inline-block text-[12px]" style={{ color: SLATE }}>Domain wird noch nicht live geschaltet.</span>
                    </div>
                  )}
                </div>
              </Section>

              <Section icon={Clock} title="Kontakt & Öffnungszeiten" testid="brocki-contact-section">
                {contact ? (
                  <div className="space-y-1.5 text-[13px]" style={{ color: INK }}>
                    {contact.phoneDisplay && <p className="flex items-center gap-2"><span style={{ color: SLATE }}>Tel.</span> {contact.phoneDisplay}</p>}
                    {contact.email && <p className="flex items-center gap-2"><span style={{ color: SLATE }}>E-Mail</span> {contact.email}</p>}
                    {contact.city && <p className="flex items-center gap-2"><MapPin size={13} style={{ color: SLATE }} /> {contact.city}</p>}
                    {Array.isArray(contact.hours) && contact.hours.length > 0 && (
                      <div className="mt-2 rounded-lg border p-2.5" style={{ borderColor: LINE }}>
                        {contact.hours.map((h, i) => (
                          <div key={i} className="flex justify-between py-0.5 text-[12.5px]">
                            <span style={{ color: MUTED }}>{h[0]}</span><span className="font-medium">{h[1]}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-[13px]" style={{ color: SLATE }}>Kontaktdaten werden nur Inhabern und Admins angezeigt.</p>
                )}
                <a href="/dashboard/oeffnungszeiten" data-testid="brocki-contact-link"
                  className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold" style={{ color: GOLD }}>
                  Öffnungszeiten & Kontakt bearbeiten <ExternalLink size={13} />
                </a>
              </Section>
            </div>
          </div>
        )}
      </div>
    </CockpitLayout>
  );
}
